import {
  actionChannel,
  call,
  delay,
  flush,
  put,
  race,
  select,
  take,
} from 'redux-saga/effects';
import * as PageRenderer from '../containers/EditorContainer/WorkspaceContainer/CanvasContainer/PageRenderer';
import {
  productInfo,
  projectMetadataSelector,
  settings,
  wrapSelector,
} from '../reducers/selectors';
import { appService, getPageForRenderer } from './zoomServices';

import { fabric } from 'fabric';
import { fromJS, isList } from 'immutable';
import PicabooConstants from '../common/PicabooConstants';
import * as Pages from '../ducks/pages';
import {
  GENERATE_THUMB_COMPLETE,
  SET_GENERATE_THUMB_COUNT,
  STOP_THUMBS,
} from '../ducks/pages';
import * as Sections from '../ducks/sections';
import SpineHelper from '../helpers/SpineHelper';
import * as Selectors from '../reducers/selectors';
import invariant from 'tiny-invariant';
import DustJacketHelper from '../helpers/DustJacketHelper';

export const FILE_TYPE_JPG = 0;
export const FILE_TYPE_PNG = 1;
function uploadFileForm(projectPK, imageData, pageID, isProjectThumb) {
  let data = new FormData();

  data.append('projectPK', projectPK);
  data.append('isProjectThumb', isProjectThumb);
  data.append('clientID', pageID);
  data.append('file', imageData, 'fileChunk');

  return data;
}

function dataURLtoBlob(dataurl) {
  let arr = dataurl.split(','),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

function uploadThumb(
  imageData,
  pageId,
  projectId,
  isProjectThumb,
  isSoftCover = false,
  imageType = FILE_TYPE_JPG,
) {
  return new Promise(function (resolve) {
    appService()
      .post(
        'api/UserImageUpload/thumb',
        uploadFileForm(
          projectId,
          imageData,
          pageId,
          isProjectThumb,
          isSoftCover,
          imageType,
        ),
      )
      .then((result) => {
        resolve(result.data);
      })
      .catch((error) => {
        console.error(error);
        resolve({ url: '' });
      });
  });
}

export function renderPageThumb({
  page,
  pageItems,
  images,
  isProjectThumb,
  projectType,
  subType = null,
  settings,
  wrap,
  overrideHeight = null,
  numPages = 20,
  info = fromJS({}),
  stamping = null,
  style = null,
  cover,
}) {
  return new Promise(function (resolve) {
    let width, height;
    height =
      isProjectThumb || projectType === PicabooConstants.PRODUCT_TYPES.PRINTS
        ? 300
        : 150;

    if (overrideHeight) {
      height = overrideHeight;
    }

    width = height / (page.get('ratio') / page.get('ratioBase'));

    const c = document.createElement('canvas');
    c.width = width;
    c.height = height;

    const canvas = new fabric.StaticCanvas(c, {
      preserveObjectStacking: true,
      stateful: false,
      renderOnAddRemove: false,
      interactive: false,
    });

    const isCard = projectType === PicabooConstants.PRODUCT_TYPES.CARD;
    let dim = PageRenderer.PageRenderer.calcPageDimensions(
      {
        clientWidth: width,
        clientHeight: height,
      },
      page.get('ratio') / page.get('ratioBase'),
      projectType,
      1,
    );
    dim.ratioBase = page.get('ratioBase') || 1;
    dim.ratioWidth = dim.width / dim.ratioBase;
    dim.scale =
      (dim.ratioWidth > dim.height || isCard ? dim.ratioWidth : dim.height) /
      PicabooConstants.PAGE.UNSCALED_PAGE_WIDTH;

    let interactive = settings.get('thumbPlaceholders');

    const renderer = new PageRenderer.PageRenderer({
      canvas, // canvas
      pageDimensions: dim, // pageDimensions
      interactive: interactive, // interactive, this should only be true for designers
      overlay: page.get('overlay'),
      isMobile: false,
      spineCaption: page.get('spineCaption'),
      numPages: numPages,
      style: style,
      subType: subType,
      wrap,
      productInfo: info,
      stamping,
      cover: cover,
      foilColor: cover,
      withWarnings: false,
    });

    renderer.drawPage(
      page,
      pageItems,
      images,
      null,
      function () {
        canvas.requestRenderAll.bind(canvas);
        resolve({ canvas, width: dim.width, height: dim.height });
      },
      false,
    );
  });
}

export function* createThumbnail(thumbnailToCreate) {
  let {
    projectPK,
    pages,
    pageItems,
    images,
    isProjectThumb,
    isSoftCover = false,
    projectType,
    cover,
    isBlank,
    pageRatio,
  } = thumbnailToCreate;

  invariant(isList(pages), 'pages must be an immutable List');

  const metadata = yield select(projectMetadataSelector);
  const style = metadata.get('style');
  const currentSettings = yield select(settings);
  const info = yield select(productInfo);
  const wrap = yield select(wrapSelector);

  for (let i = 0; i < pages.size; i++) {
    let page = pages.get(i);

    const allPages = yield select(Selectors.projectPagesSelector);
    if (allPages.filter((e) => e.pageID === page.get('id')).length === 0) {
      //The page has been removed from the project
      console.warn('tried to render a removed page', page.get('id'));
      return;
    }

    let overrideHeight = undefined;
    let resizeAfterCover = undefined;
    let thumbURL = '';
    let uploadThumbResponse = '';
    let dataURL;
    let { canvas, width } = yield call(renderPageThumb, {
      page: page,
      pageItems: pageItems,
      images: images,
      isProjectThumb: isProjectThumb,
      projectType: projectType,
      style: style,
      subType: metadata.get('subtype'),
      settings: currentSettings,
      wrap: wrap,
      numPages: metadata.get('numPages'),
      overrideHeight: overrideHeight,
      info: info,
      stamping: page.get('coverStamping'),
      cover: cover,
    });

    let canvasOpts = { format: 'jpeg', quality: 0.7 };
    if (resizeAfterCover) {
      canvasOpts.multiplier = resizeAfterCover;
    }

    let spineWidth = 0;
    //How to tell if wrapped cover... better way?
    if (wrap.get('showSpine')) {
      spineWidth = SpineHelper.calculateSpineWidth(
        width,
        metadata.get('numPages'),
        metadata.get('subtype'),
        metadata.get('style'),
        info,
      );
    }

    dataURL = canvas.toDataURL(canvasOpts);

    if (isBlank && !sessionStorage.getItem(`BLANK_THUMB_${pageRatio}`)) {
      sessionStorage.setItem(`BLANK_THUMB_${pageRatio}`, dataURL);
    }

    if (isProjectThumb) {
      let fileType = FILE_TYPE_JPG;
      let opt = { format: 'jpeg' };

      let flapWidth = DustJacketHelper.calculateDustJacketFlapWidth(
        width,
        metadata.get('numPages'),
        metadata.get('subtype'),
        info,
      );

      if (wrap.get('showSpine')) {
        opt.left = flapWidth;
        opt.width = width - 2 * flapWidth;
      }
      let projectThumbData = dataURLtoBlob(canvas.toDataURL(opt));

      uploadThumbResponse = yield call(
        uploadThumb,
        projectThumbData,
        page.get('id'),
        projectPK,
        isProjectThumb,
        isSoftCover,
        fileType,
      );
      thumbURL = uploadThumbResponse.url;
      yield call(
        handleUploadResponse,
        page.get('id'),
        thumbURL,
        uploadThumbResponse,
      );
    }

    // If the thumb was already uploaded as the project thumb, no need to upload again
    if (!thumbURL) {
      let pageID = page.get('id');
      if (isBlank && sessionStorage.getItem(`BLANK_THUMB_${pageRatio}`)) {
        dataURL = sessionStorage.getItem(`BLANK_THUMB_${pageRatio}`);
      }
      yield call(uploadAndUpdateThumb, {
        thumbURL,
        dataURL,
        pageID,
        projectPK,
      });
    }
  }
}

function* handleUploadResponse(pageID, thumbURL, uploadThumbResponse) {
  let msg = {
    type: Pages.PAGE_THUMB_UPDATED,
    pageID,
    thumbURL,
  };

  yield put(msg);
  const changeset = JSON.parse(uploadThumbResponse.changeSetResponse);
  if (changeset.resultCode === 0 && changeset.result.changes) {
    let change = changeset.result.changes.find(
      (a) => a.type === 'PAGE_LAST_UPDATE_CHANGED',
    );
    if (change) {
      msg = Pages.updatePageLastUpdate(change.changes);
      yield put(msg);
      msg = Sections.updateSectionsPageThumb(
        change.changes.sectionId,
        change.changes.pageID,
        thumbURL,
        change.changes.numBlankPages,
      );
      yield put(msg);
    }
  }
}

export function* uploadAndUpdateThumb({ dataURL, pageID, projectPK }) {
  let uploadThumbResponse, thumbURL;

  const thumbData = dataURLtoBlob(dataURL);
  uploadThumbResponse = yield call(
    uploadThumb,
    thumbData,
    pageID,
    projectPK,
    false,
  );
  thumbURL = uploadThumbResponse.url;

  if (thumbURL) {
    yield call(handleUploadResponse, pageID, thumbURL, uploadThumbResponse);
  }
}

export function* requestThumbnail(
  projectId,
  spread,
  page,
  section,
  isProjectThumb,
  cropCover,
  isPrints,
  isBlank,
  pageRatio,
) {
  yield put({
    type: THUMB_REQUEST,
    projectId,
    spread,
    page,
    section,
    isProjectThumb,
    cropCover,
    isPrints,
    isBlank,
    pageRatio,
  });
}

const THUMB_REQUEST = 'THUMB_REQUEST';
export function* watchThumbRequest() {
  const requestChannel = yield actionChannel(THUMB_REQUEST);
  while (true) {
    const payload = yield take(requestChannel);

    const { cancel } = yield race({
      thumb: call(createThumbnailOnHiddenCanvas, payload),
      cancel: take(STOP_THUMBS),
      timeOut: delay(10000),
    });

    if (cancel !== undefined) {
      yield put({ type: SET_GENERATE_THUMB_COUNT, count: 0 });
      yield flush(requestChannel);
    }

    yield delay(100);
  }
}

export function* createThumbnailOnHiddenCanvas(payload) {
  try {
    let {
      projectId,
      page: pageID,
      section,
      isProjectThumb,
      isBlank,
      pageRatio,
    } = payload;
    if (isBlank && sessionStorage.getItem(`BLANK_THUMB_${pageRatio}`)) {
      let dataURL = sessionStorage.getItem(`BLANK_THUMB_${pageRatio}`);
      yield call(uploadAndUpdateThumb, {
        dataURL,
        pageID,
        projectPK: projectId,
      });
      yield put({ type: GENERATE_THUMB_COMPLETE });
      return;
    }

    const metadata = yield select(projectMetadataSelector);
    let pageForRender = yield getPageForRenderer(projectId, pageID);

    let pageItems = pageForRender.pageItems;
    if (pageItems) {
      Object.keys(pageItems).forEach((k) => {
        let pageItem = pageItems[k];
        pageItem.pageID = pageID;
      });
    }

    const thumbnailToCreate = {};
    thumbnailToCreate.projectPK = metadata.get('projectPK');
    let thePage = pageForRender.pages[pageID];
    let iPage = fromJS(thePage);
    let thePages = [iPage];
    thumbnailToCreate.pages = fromJS(thePages);
    thumbnailToCreate.pageItems = fromJS(pageItems);
    thumbnailToCreate.images = fromJS(pageForRender.images);
    thumbnailToCreate.isProjectThumb = isProjectThumb;
    thumbnailToCreate.isSoftCover = false;
    thumbnailToCreate.projectType = metadata.get('type');
    thumbnailToCreate.wrap = fromJS(pageForRender.wrap);
    thumbnailToCreate.sectionID = section;
    thumbnailToCreate.isBlank = isBlank;
    thumbnailToCreate.pageRatio = pageRatio;

    yield call(createThumbnail, thumbnailToCreate);
    yield put({ type: GENERATE_THUMB_COMPLETE });
  } catch (e) {
    console.error('Could not create thumb for', payload, e);
  }
}
